import React, { useEffect, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { Home as HomeIcon, Navigation, Trophy, User as UserIcon, Bot, ShieldCheck, TrendingUp } from "lucide-react";
import { useUser } from "@/context/UserContext";
import { useI18n } from "@/i18n/I18nContext";
import { findTodayTrip, subscribeMyRoutes } from "@/lib/myRoutes";
import { isProfileIncomplete } from "@/lib/profileStatus";

// Barra de pestañas inferior — SOLO móvil (sm:hidden). En escritorio la
// navegación sigue en la barra superior. Máximo 5 pestañas fijas, sin
// scroll: navegación que hay que descubrir es navegación rota.
const MAX_TABS = 5;

// El viaje de hoy, con la lista de rutas que ya consultan la home y el aviso
// de chat. Se recalcula cada minuto (un viaje deja de contar 2 h después).
const useTodayTrip = (userId) => {
  const [routes, setRoutes] = useState([]);
  const [now, setNow] = useState(() => new Date());
  useEffect(() => subscribeMyRoutes(setRoutes), []);
  useEffect(() => {
    const iv = setInterval(() => setNow(new Date()), 60000);
    return () => clearInterval(iv);
  }, []);
  return findTodayTrip(routes, userId, now);
};

const BottomNav = () => {
  const { user } = useUser();
  const { t } = useI18n();
  const location = useLocation();
  const isGuest = user?.role === "pmst_guest";
  const trip = useTodayTrip(isGuest ? null : user?.id);

  const isHr = user?.role === "hr_manager" || user?.role === "super_admin";
  const isSuperAdmin = user?.role === "super_admin";

  // Durante el alta, las pestañas solo devolvían al alta. Las personas
  // externas del plan de movilidad solo tienen el plan: sin barra.
  if (!user || location.pathname === "/login" || user.is_onboarded === false || isGuest) return null;

  const home = { to: "/", end: true, icon: HomeIcon, label: t("nav.home"), testid: "bottomnav-home" };
  const ranking = { to: "/ranking", icon: Trophy, label: t("nav.ranking"), testid: "bottomnav-ranking" };
  const advisor = { to: "/advisor", icon: Bot, label: t("nav.advisor"), testid: "bottomnav-advisor" };
  const profile = {
    to: "/profile", icon: UserIcon, label: t("nav.profile"), testid: "bottomnav-profile",
    // Punto si falta el domicilio o el DNI (lo mismo que avisa el perfil)
    dot: isProfileIncomplete(user),
  };

  // Empleado: lo esencial del día a día. RRHH añade sus herramientas.
  // Super admin: analítica en lugar del ranking.
  const tabs = isSuperAdmin
    ? [home, advisor, { to: "/admin", icon: ShieldCheck, label: t("nav.admin_super"), testid: "bottomnav-admin" },
       { to: "/super/analytics", icon: TrendingUp, label: t("nav.super_analytics_short"), testid: "bottomnav-analytics" }, profile]
    : isHr
    ? [home, ranking, advisor, { to: "/admin", icon: ShieldCheck, label: t("nav.admin_hr"), testid: "bottomnav-admin" }, profile]
    : [home, ranking, profile];

  // Sin ranking si la empresa ha desactivado la gamificación
  if (user.is_gamification_enabled === false) {
    const i = tabs.findIndex((tab) => tab.testid === "bottomnav-ranking");
    if (i >= 0) tabs.splice(i, 1);
  }

  // "Mi viaje" solo cuando hay un viaje hoy: lleva directo a la pantalla del
  // viaje (QR, chat, navegación). Si no cabe, deja fuera la pestaña menos
  // necesaria durante un trayecto.
  if (trip) {
    tabs.splice(1, 0, {
      to: `/conduccion/${trip.id}`, icon: Navigation, label: t("nav.my_trip"), testid: "bottomnav-trip", live: true,
    });
    if (tabs.length > MAX_TABS) {
      const drop = tabs.findIndex((tab) => ["bottomnav-ranking", "bottomnav-advisor", "bottomnav-analytics"].includes(tab.testid));
      if (drop >= 0) tabs.splice(drop, 1);
    }
  }

  return (
    <nav
      data-testid="bottom-nav"
      className="sm:hidden fixed inset-x-3 bottom-[calc(0.75rem+env(safe-area-inset-bottom))] z-30 rounded-[1.4rem] bg-colygon-darkest ring-1 ring-white/10 shadow-[0_18px_40px_-12px_rgba(0,40,15,0.6)]"
    >
      {/* Barra flotante: no ocupa todo el borde inferior y deja ver el
          contenido detrás; la pestaña activa lleva su píldora. */}
      <div className="flex items-stretch px-1.5 py-1.5">
        {tabs.map(({ to, end, icon: Icon, label, testid, dot, live }) => (
          <NavLink
            key={testid}
            to={to}
            end={end}
            data-testid={testid}
            className={({ isActive }) =>
              `flex-1 min-w-0 flex flex-col items-center justify-center gap-1 py-1.5 rounded-2xl transition-colors ${
                isActive ? "bg-white/[0.12] text-colygon-light" : live ? "text-lime-300 active:text-lime-200" : "text-white/80 active:text-white"
              }`
            }
          >
            {({ isActive }) => (
              <>
                <span className="relative">
                  <Icon className="h-5 w-5" strokeWidth={isActive ? 2.4 : 2} />
                  {live && !isActive && (
                    <span className="absolute -top-0.5 -right-1 h-2 w-2 rounded-full bg-lime-400 motion-safe:animate-pulse" aria-hidden="true" />
                  )}
                  {dot && (
                    <>
                      <span data-testid={`${testid}-dot`} className="absolute -top-0.5 -right-1 h-2 w-2 rounded-full bg-amber-400 ring-2 ring-colygon-darkest" aria-hidden="true" />
                      <span className="sr-only">{t("nav.profile_incomplete")}</span>
                    </>
                  )}
                </span>
                {/* Etiqueta activa en blanco: el verde lima del icono no llega al contraste de texto */}
                <span className={`text-[0.6875rem] font-bold leading-none truncate max-w-full px-1 ${isActive ? "text-white" : ""}`}>
                  {label}
                </span>
              </>
            )}
          </NavLink>
        ))}
      </div>
    </nav>
  );
};

export default BottomNav;
